<!--
  SPOSTAMENTO INTERNO — il form, senza la pagina attorno.

  Era dentro `JourneyPage`, che è una rotta a sé. Da quando «Nuova
  prenotazione» apre gli altri servizi sotto la scelta del servizio, lo
  spostamento interno doveva fare lo stesso: qui c'è solo il form (data,
  corse, salva), così lo usano sia la pagina sia il passo 2 di Nuova
  prenotazione, senza due copie che si allineano a memoria.

  Chi lo ospita decide dove andare dopo il salvataggio: il componente emette
  `salvato` con la data dello spostamento creato.
-->
<template>
  <div class="jf">
    <div class="jf__datebar">
      <el-form
        ref="dataForm"
        :inline="true"
        :model="formData"
        :rules="formRuleData"
      >
        <el-form-item
          label="Data"
          prop="journeyDate"
        >
          <!-- CampoData: si apre cliccando su tutto il campo, non solo
               sull'icona. -->
          <CampoData
            v-model="formData.journeyDate"
            format="DD/MM/YYYY"
            value-format="YYYY-MM-DD"
            type="date"
            placeholder="Seleziona una data"
            :disabled-date="giornoPassato"
          />
        </el-form-item>
        <!-- Nessun tetto di spostamenti al giorno: la disponibilità la verifica
             ogni corsa, per provincia, nell'editor qui sotto. -->
      </el-form>
    </div>

    <!-- Cosa c'è GIÀ quel giorno: si compila un orario sapendo quali corse
         sono in programma, senza aprire l'agenda in un'altra scheda. Sono
         quelle che l'utente può vedere — le corse delle sue sedi e quelle che
         ha inserito lui — perché il filtro lo fa il server.
         Se quel giorno è libero il blocco non compare: un riquadro che dice
         «non c'è niente» toglie solo spazio alla prima corsa da inserire. -->
    <section
      v-if="mostraGiorno"
      class="jf__giorno"
    >
      <header class="jf__giornoHead">
        <h3>Già in programma</h3>
        <span class="jf__giornoCount">
          {{ righeGiorno.length }} {{ righeGiorno.length === 1 ? 'corsa' : 'corse' }}
        </span>
      </header>
      <p
        v-if="erroreGiorno"
        class="jf__giornoErr"
      >
        {{ erroreGiorno }}
      </p>
      <AgendaRighe
        v-else
        :righe="righeGiorno"
        :loading="caricamentoGiorno"
      />
    </section>

    <!-- Unico rendering path delle Corse: stesso editor dell'edit mode. -->
    <journey-legs-editor
      ref="legsEditor"
      :date="formData.journeyDate || ''"
      :giornata="spostamentiGiorno"
    >
      <!-- «Salva» a destra del totale delle corse. -->
      <template #azioni>
        <el-button
          type="primary"
          :loading="salvataggio"
          @click="saveJourney"
        >
          Salva
        </el-button>
      </template>
    </journey-legs-editor>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';
import journeyAPI from '../services/journeyAPI';
import partnerAPI from '@/services/partnerAPI';
import { isLoggedStore } from '../store/logged';
import { ElMessage } from 'element-plus';
import dayjs from 'dayjs';
import JourneyLegsEditor from './JourneyLegsEditor.vue';
import CampoData from './ui/CampoData.vue';
import AgendaRighe from './AgendaRighe.vue';
import agendaAPI from '../services/agendaAPI';
import { buildRows } from '../services/agendaRows';

export default {
  name: 'JourneyFormComponent',
  components: { JourneyLegsEditor, CampoData, AgendaRighe },

  props: {
    // Data scelta altrove (giorno del calendario dell'Agenda, rotta
    // /journey/:date). Vuota = la sceglie l'operatore qui.
    dataIniziale: {
      type: String,
      default: '',
    },
  },

  emits: ['salvato'],

  setup(props, { emit }) {
    const storeLogin = isLoggedStore();

    const legsEditor = ref(null);
    const dataValida = (d) => /^\d{4}-\d{2}-\d{2}$/.test(String(d || ''));
    // Il partner inserisce da oggi in poi: i giorni passati li registra Simba.
    // Un giorno passato che arriva dal calendario non si precompila.
    const giornoPassato = (d) => dayjs(d).isBefore(dayjs(), 'day');
    const dataInseribile = (d) => dataValida(d) && !giornoPassato(d);
    const formData = ref({
      journeyDate: dataInseribile(props.dataIniziale) ? props.dataIniziale : null,
    });
    const dataForm = ref(null);
    const salvataggio = ref(false);

    // Corse già in programma nella data scelta: prenotazioni e spostamenti
    // insieme, come in Agenda. Si ricaricano a ogni cambio di data; una
    // richiesta più lenta che torna dopo un altro cambio non sovrascrive le
    // righe di quella nuova.
    const righeGiorno = ref([]);
    // Gli spostamenti di quel giorno con le corse: il driver può rientrare a
    // bordo di una di quelle (un'auto aggiunta in un secondo momento).
    const spostamentiGiorno = ref([]);
    const caricamentoGiorno = ref(false);
    const erroreGiorno = ref('');
    let ultimaRichiesta = 0;

    // Il blocco si vede solo se ha qualcosa da dire: corse in programma o
    // l'errore di caricamento. Il caricamento non lo fa comparire, altrimenti
    // su un giorno libero si vedrebbe apparire e sparire un riquadro vuoto.
    const mostraGiorno = computed(
      () => Boolean(erroreGiorno.value) || righeGiorno.value.length > 0
    );

    const caricaGiorno = async (giorno) => {
      if (!dataValida(giorno)) {
        righeGiorno.value = [];
        spostamentiGiorno.value = [];
        erroreGiorno.value = '';
        return;
      }
      const richiesta = ++ultimaRichiesta;
      caricamentoGiorno.value = true;
      erroreGiorno.value = '';
      try {
        const { bookings, journeys } = await agendaAPI.agendaInWindow(giorno, giorno);
        if (richiesta !== ultimaRichiesta) return;
        righeGiorno.value = buildRows({ bookings, journeys });
        spostamentiGiorno.value = journeys || [];
      } catch (error) {
        if (richiesta !== ultimaRichiesta) return;
        console.error('Corse del giorno non caricate:', error);
        righeGiorno.value = [];
        erroreGiorno.value = 'Non sono riuscito a caricare le corse di quel giorno.';
      } finally {
        if (richiesta === ultimaRichiesta) caricamentoGiorno.value = false;
      }
    };

    watch(() => formData.value.journeyDate, caricaGiorno, { immediate: true });
    const formRuleData = {
      journeyDate: [
        { required: true, message: 'Seleziona una data!', trigger: 'blur' },
      ],
    };

    // La data può arrivare dopo il montaggio (la pagina la legge dalla rotta).
    watch(
      () => props.dataIniziale,
      (d) => {
        if (dataInseribile(d)) formData.value.journeyDate = d;
      }
    );

    const saveJourney = async () => {
      if (!formData.value.journeyDate) {
        ElMessage({ message: 'Seleziona una data', type: 'error' });
        return;
      }
      const editor = legsEditor.value;
      if (!editor) return;
      const check = editor.validate();
      if (!check.ok) {
        ElMessage({ message: check.message, type: 'error' });
        return;
      }
      salvataggio.value = true;
      try {
        const responsePartner = await partnerAPI.getPartnerByEmployee(
          storeLogin.employee.id
        );
        const partner = responsePartner.data[0].headquarters[0].partner;
        const resp = await journeyAPI.addJourney(
          editor.buildJourneyPayload(partner)
        );
        if (resp.status === 201 || resp.status === 200) {
          ElMessage({
            message: 'Spostamento interno salvato',
            type: 'success',
          });
          emit('salvato', formData.value.journeyDate);
        }
      } catch (error) {
        // 400 (disponibilità, validazioni) e 403 (capability): il toast
        // col messaggio del server arriva dall'interceptor di response
        console.error('saveJourney:', error?.response?.status);
      } finally {
        salvataggio.value = false;
      }
    };

    return {
      legsEditor,
      formData,
      dataForm,
      formRuleData,
      salvataggio,
      saveJourney,
      giornoPassato,
      righeGiorno,
      spostamentiGiorno,
      caricamentoGiorno,
      erroreGiorno,
      mostraGiorno,
    };
  },
};
</script>

<style scoped>
.jf { text-align: left; }

/* La data è il primo dato da scegliere: sta a sinistra come tutto il resto,
   non centrata in mezzo alla pagina. */
.jf__datebar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-bottom: 20px;
}
.jf__alert {
  margin-top: 8px;
}

/* Allineato alla colonna delle corse: l'editor tiene 50px a sinistra per la
   riga numerata della timeline, e «Aggiungi corsa» parte da lì. Un Salva più a
   sinistra di tutto il resto sembrava appoggiato al bordo della card. */

/* Cosa c'è già quel giorno: un blocco di lettura, staccato dal form ma meno
   in evidenza delle corse che si stanno inserendo. Sotto ci vuole aria: con
   4px il riquadro sembrava la testata della prima corsa da inserire. */
.jf__giorno {
  margin: 12px 0 28px;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 10px;
  background: var(--bg-secondary, #f8fafc);
  overflow: hidden;
}
.jf__giornoHead {
  display: flex; align-items: baseline; justify-content: space-between;
  gap: 12px; padding: 10px 14px;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
}
.jf__giornoHead h3 { margin: 0; font-size: 14px; font-weight: 600; }
.jf__giornoCount { font-size: 12px; color: var(--text-tertiary, #6b7280); }
.jf__giornoErr { margin: 0; padding: 12px 14px; font-size: 13px; color: #b91c1c; }
</style>
